/**
 * Pure view model for the pool settings pane.
 *
 * This module is pure: no React import, no DOM, no fetch. It holds the ten
 * §27.2 card slots and decides which of them the screen actually renders.
 *
 * TWO RULES DECIDE THAT, and they are separate on purpose.
 *
 * 1. `hasContent`. A slot whose content has not been built renders NOTHING,
 *    not an empty card. The screen used to ship every slot as a header, on the
 *    theory that a reserved header showed where a feature was going. In front
 *    of a fan it did the opposite: four cards titled Members, Contests, Chat
 *    and Notifications that answer nothing when tapped read as four broken
 *    features, not four unbuilt ones. The slots stay listed here, with their
 *    owners, so the day one is built the change is flipping one flag.
 * 2. `isDanger`. Mirrors `POOL_ROLE_RANK` in
 *    `packages/api-core/src/auth/types.ts`: both Danger zone actions
 *    (`/ownership/transfer` and `/pool/archive`) require `owner` rank on the
 *    server, while every other card's underlying write only requires `admin`,
 *    so an Admin is never shown a control the server will refuse.
 */
import { isPoolNoAccessCode } from "../../errors/poolAccess";
import type { typePoolSettingsPanel } from "./poolSettingsRoute";

export const POOL_SETTINGS_LOADING_LABEL = "Loading pool settings";
export const POOL_SETTINGS_FAILED_MESSAGE = "We could not load these settings.";
export const POOL_SETTINGS_RETRY_LABEL = "Try again";
export const POOL_SETTINGS_ROLE_TITLE =
  "Only the Commissioner and Admins can open pool settings.";
export const POOL_SETTINGS_ROLE_BODY =
  "You're a member of this pool, so you can still see everything on the pool's home.";
export const POOL_SETTINGS_ROLE_ACTION_LABEL = "Back to pool";

export type typePoolSettingsRole = "owner" | "admin" | "member";

export type typePoolSettingsSlot = {
  panel: typePoolSettingsPanel;
  title: string;
  /** True only for `danger-zone`. Drives the separated, error-toned treatment. */
  isDanger: boolean;
  /**
   * True when `PoolSettingsPane` passes this slot real children. False means
   * the slot is reserved and unbuilt, and the screen renders no card for it at
   * all. Kept next to `ownedBy` because the two answer the same question from
   * either side: what is here now, and who is bringing the rest.
   *
   * This flag and the branch in `PoolSettingsPane` must move together. A slot
   * marked true with no branch renders an empty card again, which is the whole
   * defect this flag exists to prevent.
   */
  hasContent: boolean;
  /** The issue that owns this slot's content, or null when no issue is filed. */
  ownedBy: string | null;
};

export const POOL_SETTINGS_SLOTS: readonly typePoolSettingsSlot[] = [
  {
    panel: "pool",
    title: "Pool",
    isDanger: false,
    hasContent: true,
    ownedBy: "ISSUE-201",
  },
  {
    panel: "joining",
    title: "Joining",
    isDanger: false,
    hasContent: true,
    ownedBy: "ISSUE-192",
  },
  {
    panel: "contest-rules",
    title: "Contest rules",
    isDanger: false,
    hasContent: true,
    ownedBy: "ISSUE-200",
  },
  {
    // UNOWNED. ISSUE-817 held this slot while it was the placeholder for the
    // member role actions, and then shipped them on the members pane at
    // `/pools?poolId=&view=members` instead, that pane is member-visible,
    // and this pane returns `noAccess` for a plain member. Nothing is planned
    // for a Members card HERE. Left null rather than pointing at a closed
    // card, which no future pass would think to re-open.
    panel: "members",
    title: "Members",
    isDanger: false,
    hasContent: false,
    ownedBy: null,
  },
  {
    panel: "contests",
    title: "Contests",
    isDanger: false,
    hasContent: true,
    ownedBy: "ISSUE-1042",
  },
  {
    panel: "season",
    title: "Season",
    isDanger: false,
    hasContent: true,
    ownedBy: "ISSUE-501",
  },
  {
    panel: "awards",
    title: "Awards",
    isDanger: false,
    hasContent: true,
    ownedBy: "ISSUE-482",
  },
  {
    panel: "chat",
    title: "Chat",
    isDanger: false,
    hasContent: true,
    ownedBy: "ISSUE-1040",
  },
  {
    panel: "notifications",
    title: "Notifications",
    isDanger: false,
    hasContent: false,
    ownedBy: "ISSUE-490",
  },
  {
    panel: "danger-zone",
    title: "Danger zone",
    isDanger: true,
    hasContent: true,
    ownedBy: "ISSUE-817",
  },
];

export type typePoolSettingsState =
  | { phase: "loading" }
  | { phase: "failed" }
  | { phase: "noAccess"; reason: "membership" | "role" }
  | {
      phase: "ready";
      poolName: string;
      viewerPoolRole: "owner" | "admin";
      slots: readonly typePoolSettingsSlot[];
    };

/**
 * The built slots, before the role rule. This is what the loading skeletons
 * count, because the viewer's role is exactly what `/pool/get` has not
 * returned yet while they are on screen. It overcounts by one for an Admin,
 * which settles by removing a skeleton; counting every slot would have settled
 * by removing five, and a screen that deletes most of what it promised while
 * you watch reads as a failure.
 */
export const POOL_SETTINGS_BUILT_SLOTS: readonly typePoolSettingsSlot[] =
  POOL_SETTINGS_SLOTS.filter((slot) => slot.hasContent);

/**
 * The slots this screen renders, in order. Unbuilt slots are dropped for
 * everybody; the Danger zone is dropped for an Admin.
 */
export function visibleSlotsForRole(
  role: "owner" | "admin",
): readonly typePoolSettingsSlot[] {
  return POOL_SETTINGS_SLOTS.filter(
    (slot) => slot.hasContent && (role === "owner" || !slot.isDanger),
  );
}

export function resolvePoolSettingsState(input: {
  isLoading: boolean;
  isTransportError: boolean;
  errorCode: string | null;
  poolName: string | null;
  viewerPoolRole: typePoolSettingsRole | null;
}): typePoolSettingsState {
  if (isPoolNoAccessCode(input.errorCode)) {
    return { phase: "noAccess", reason: "membership" };
  }
  if (input.isTransportError || input.errorCode !== null) {
    return { phase: "failed" };
  }
  if (input.isLoading) {
    return { phase: "loading" };
  }
  if (input.poolName === null || input.viewerPoolRole === null) {
    return { phase: "failed" };
  }
  if (input.viewerPoolRole === "member") {
    return { phase: "noAccess", reason: "role" };
  }
  return {
    phase: "ready",
    poolName: input.poolName,
    viewerPoolRole: input.viewerPoolRole,
    slots: visibleSlotsForRole(input.viewerPoolRole),
  };
}
